<template>
  <div class="memorial-selector">
    <v-card elevation="0">
      <v-card-title class="text-h5 text-center pa-6">
        Select Memory
      </v-card-title>
      
      <v-card-text class="pa-6">
        <p class="text-body-1 text-center mb-6">
          You have multiple memories. Please select one to continue:
        </p>
        
        <v-list>
          <v-list-item
            v-for="memorial in memorials"
            :key="memorial.uuid"
            @click="selectMemorial(memorial)"
            class="memorial-item"
            rounded
          >
            <template v-slot:prepend>
              <v-icon color="primary">mdi-heart-outline</v-icon>
            </template>
            
            <v-list-item-title class="text-h6">
              {{ memorial.nameOfPerson }}
            </v-list-item-title>
            
            <v-list-item-subtitle>
              <div class="memorial-details">
                <div v-if="memorial.address" class="text-caption">
                  <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                  {{ memorial.address }}
                </div>
                <div class="text-caption">
                  <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
                  {{ formatMemorialDateLabel(memorial) }}
                </div>
                <v-chip
                  v-if="memorial.paymentCompleted || memorial.stripeCustomerId"
                  color="success"
                  size="small"
                  class="mt-1"
                >
                  <v-icon left size="small">mdi-check</v-icon>
                  Paid
                </v-chip>
                <v-chip
                  v-else
                  color="warning"
                  size="small"
                  class="mt-1"
                >
                  <v-icon left size="small">mdi-clock-outline</v-icon>
                  Payment Pending
                </v-chip>
              </div>
            </v-list-item-subtitle>
            
            <template v-slot:append>
              <v-icon>mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
        </v-list>

        <v-divider v-if="adminMemorials.length" class="my-4"></v-divider>
        <p v-if="adminMemorials.length" class="text-subtitle-2 text-center mb-4">
          Memories You Administer
        </p>

        <v-list v-if="adminMemorials.length">
          <v-list-item
            v-for="memorial in adminMemorials"
            :key="memorial.uuid"
            @click="selectAdmin(memorial)"
            class="memorial-item"
            rounded
          >
            <template v-slot:prepend>
              <v-icon color="primary">mdi-shield-account</v-icon>
            </template>

            <v-list-item-title class="text-h6">
              {{ memorial.nameOfPerson }}
            </v-list-item-title>

            <v-list-item-subtitle>
              <div class="memorial-details">
                <div class="text-caption">Administrator</div>
              </div>
            </v-list-item-subtitle>

            <template v-slot:append>
              <v-icon>mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
        </v-list>

        <v-divider v-if="contributions.length" class="my-4"></v-divider>
        <p v-if="contributions.length" class="text-subtitle-2 text-center mb-4">
          Contributions
        </p>

        <v-list v-if="contributions.length">
          <v-list-item
            v-for="contribution in contributions"
            :key="contribution.uuid || contribution.id"
            @click="selectContribution(contribution)"
            class="memorial-item"
            rounded
          >
            <template v-slot:prepend>
              <v-icon color="primary">mdi-heart-outline</v-icon>
            </template>

            <v-list-item-title class="text-h6">
              {{ contribution.memorial?.nameOfPerson || 'Memory' }}
            </v-list-item-title>

            <v-list-item-subtitle>
              <div class="memorial-details">
                <div v-if="contribution.memorial?.address" class="text-caption">
                  <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                  {{ contribution.memorial.address }}
                </div>
                <div class="text-caption">
                  <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
                  Contributed {{ formatDate(contribution.dateCreated || contribution.createdAt) }}
                </div>
              </div>
            </v-list-item-subtitle>

            <template v-slot:append>
              <v-icon>mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
// defineProps and defineEmits are compiler macros, no import needed

defineProps({
  memorials: {
    type: Array,
    required: true
  },
  adminMemorials: {
    type: Array,
    default: () => []
  },
  contributions: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['select', 'selectAdmin', 'selectContribution'])

const selectMemorial = (memorial) => {
  emit('select', memorial)
}

const selectAdmin = (memorial) => {
  emit('selectAdmin', memorial)
}

const selectContribution = (contribution) => {
  emit('selectContribution', contribution)
}

const formatDate = (dateString) => {
  if (!dateString) return null

  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    return null
  }
}

const formatMemorialDateLabel = (memorial) => {
  const memorialDate = formatDate(memorial?.memorialDate)
  if (memorialDate) {
    return memorialDate
  }

  const createdAt = formatDate(memorial?.dateCreated)
  if (createdAt) {
    return `Created ${createdAt}`
  }

  return 'Unknown'
}
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.memorial-selector {
  .memorial-item {
    @include card-hover;
    margin-bottom: 1rem;
    border: $border-width solid $border;
    
    &:hover {
      cursor: pointer;
      transform: translateY(-2px);
      transition: transform 0.2s ease;
    }
  }
  
  .memorial-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

@include mobile {
  .memorial-item {
    margin-bottom: 0.5rem;
  }
}
</style>
